import { useState } from "react";
import { Mail, MapPin, Clock, Send, Smartphone, ExternalLink } from "lucide-react";
import { school } from "../mock/mock";
import { useToast } from "../hooks/use-toast";
import ReceptionStatus from "../components/ReceptionStatus";
import SwishLogo from "../components/SwishLogo";
import useSeo from "../hooks/useSeo";
import { Link } from "react-router-dom";

export default function Kontakt() {
  useSeo({ title: "Kontakt", description: "Kontakta City Trafikskola Uppsala – Torkelsgatan 29 vid Kvarntorget. Telefon 018-265 06 46, info@citytrafikskolauppsala.se. Körtider mån–fre 07:30–17:40." });
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("request failed");
      toast({ title: "Tack för ditt meddelande!", description: "Vi återkommer så snart vi kan." });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast({ title: "Kunde inte skicka", description: `Försök igen eller mejla oss på ${school.email}.`, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-20 md:pt-[8.25rem]">
      <section className="bg-[#0B2545] py-20">
        <div className="max-w-7xl mx-auto px-5 text-center">
          <span className="text-[#ffce3a] font-semibold tracking-[0.2em] text-xs uppercase">Kontakt</span>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-white mt-3">Hitta till oss</h1>
          <p className="text-slate-300 mt-4 max-w-xl mx-auto">Vi finns i fräscha lokaler vid {school.area}. Välkommen att boka tid för inskrivning, riskutbildning eller andra frågor!</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Info */}
          <div>
            <span className="text-[#1B6CA8] font-semibold tracking-[0.18em] text-xs uppercase">Kontaktuppgifter</span>
            <h2 className="font-display text-3xl font-bold text-[#0B2545] mt-2">{school.legalName}</h2>
            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-4">
                <span className="w-12 h-12 rounded-xl bg-[#f0f5fb] text-[#1B6CA8] flex items-center justify-center flex-shrink-0"><MapPin size={22} /></span>
                <div>
                  <div className="text-sm text-slate-400 font-semibold">Adress</div>
                  <div className="text-[#0B2545] font-semibold">{school.address}, {school.postal}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="w-12 h-12 rounded-xl bg-[#f0f5fb] text-[#1B6CA8] flex items-center justify-center flex-shrink-0"><Mail size={22} /></span>
                <div>
                  <div className="text-sm text-slate-400 font-semibold">E-post</div>
                  <a href={`mailto:${school.email}`} className="text-[#0B2545] font-semibold hover:text-[#1B6CA8] transition-colors break-all">{school.email}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="w-12 h-12 rounded-xl bg-[#f0f5fb] flex items-center justify-center flex-shrink-0"><Smartphone size={22} className="text-[#1B6CA8]" /></span>
                <div>
                  <div className="text-sm text-slate-400 font-semibold">Betala med Swish</div>
                  <div className="flex items-center gap-3 mt-1">
                    <SwishLogo className="h-7" />
                    <span className="text-[#0B2545] font-bold text-lg tracking-wide" data-testid="swish-number">{school.swish}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="w-12 h-12 rounded-xl bg-[#f0f5fb] text-[#1B6CA8] flex items-center justify-center flex-shrink-0"><Clock size={22} /></span>
                <div>
                  <div className="text-sm text-slate-400 font-semibold">{school.hoursTitle}</div>
                  {school.hours.map((h) => (
                    <div key={h.day} className="text-[#0B2545] text-sm"><span className="font-semibold">{h.day}:</span> {h.time}</div>
                  ))}
                  <ReceptionStatus />
                </div>
              </div>
            </div>
            <a
              href={school.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-xl bg-[#ffce3a] text-[#0B2545] font-semibold hover:bg-[#ffd85f] transition-colors"
            >
              Boka online – dygnet runt <ExternalLink size={16} />
            </a>
            <div className="mt-8 rounded-2xl overflow-hidden border border-slate-100 h-64">
              <iframe
                title="Karta"
                src="https://www.google.com/maps?q=Torkelsgatan+29,+754+21+Uppsala&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-100 p-8 h-fit">
            <h3 className="font-display text-xl font-bold text-[#0B2545]">Skicka ett meddelande</h3>
            <p className="text-slate-500 text-sm mt-1 mb-6">Fyll i formuläret så återkommer vi så snart vi kan.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1.5">Namn *</label>
                <input data-testid="contact-name-input" name="name" value={form.name} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1B6CA8] focus:ring-2 focus:ring-[#1B6CA8]/20 outline-none transition" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-1.5">E-post *</label>
                  <input data-testid="contact-email-input" type="email" name="email" value={form.email} onChange={handleChange} required className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1B6CA8] focus:ring-2 focus:ring-[#1B6CA8]/20 outline-none transition" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-600 mb-1.5">Telefon</label>
                  <input data-testid="contact-phone-input" name="phone" value={form.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1B6CA8] focus:ring-2 focus:ring-[#1B6CA8]/20 outline-none transition" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-600 mb-1.5">Meddelande *</label>
                <textarea data-testid="contact-message-input" name="message" value={form.message} onChange={handleChange} required rows={5} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#1B6CA8] focus:ring-2 focus:ring-[#1B6CA8]/20 outline-none transition resize-none" />
              </div>
              <button type="submit" disabled={sending} data-testid="contact-submit-btn" className="group w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#ffce3a] text-[#0B2545] font-semibold hover:bg-[#ffd85f] transition-colors disabled:opacity-60">
                {sending ? "Skickar…" : "Skicka meddelande"}
                <Send size={17} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="text-xs text-slate-500 mt-3" data-testid="contact-gdpr-note">Genom att skicka godkänner du att vi behandlar dina uppgifter för att svara på ditt meddelande, enligt vår <Link to="/integritetspolicy" className="font-semibold text-[#1B6CA8] underline underline-offset-2">integritetspolicy</Link>.</p>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}
